import { addMethod, string, object, boolean, mixed } from 'yup';
import Api from 'api';
import { corporateValidation, calculateFileSizeTitle } from './utils';
import { fileSizeLimit } from './constatnts';
import { storage } from 'store';
import { getCachedDnsCheck, setCachedDnsCheck } from './services';

export const basicEmailValidation = value => {
  const emailRegex =
    /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-z0-9.-]{1,63}\.[a-z]{2,6}$/;

  if (!emailRegex.test(value)) {
    storage.addItem('isEmailCheckedSuccessful', false);
    return false;
  }

  return true;
};

const emailDnsValidation = async email => {
  const cachedResult = getCachedDnsCheck(email);

  if (cachedResult !== undefined) {
    storage.addItem('isEmailCheckedSuccessful', cachedResult);
    return cachedResult;
  }

  try {
    storage.addItem('isEmailCheckLoading', true);
    storage.addItem('isEmailCheckedSuccessful', false);

    const dnsCheck = await Api.getDnsCheck(email);

    const checkStatus = dnsCheck?.status || false;
    setCachedDnsCheck(email, checkStatus);
    storage.addItem('isEmailCheckedSuccessful', checkStatus);

    return checkStatus;
  } catch (error) {
    storage.addItem('isEmailCheckedSuccessful', false);
    setCachedDnsCheck(email, false);

    return false;
  } finally {
    storage.addItem('isEmailCheckLoading', false);
  }
};

export const emailValidation = async value => {
  const isBasicValid = basicEmailValidation(value);

  if (isBasicValid) {
    return await emailDnsValidation(value);
  }

  return false;
};

const phoneValidation = value => {
  if (!value) {
    return false;
  }

  return value.length >= 5 && value.length <= 18;
};

const fileSizeValidation = (value, obj, messages) => {
  const indexOfFileTooLarge = value?.findIndex(({ size }) => size >= fileSizeLimit);
  const indexOfFileTooSmall = value?.findIndex(({ size }) => size < 0);

  if (indexOfFileTooLarge !== -1) {
    return obj.createError({
      message: `${messages.size} (${calculateFileSizeTitle(value?.[indexOfFileTooLarge].size)})`,
    });
  }

  if (indexOfFileTooSmall !== -1) {
    return obj.createError({
      message: messages.smallSize,
    });
  }

  return true;
};

const fileTypeValidation = (value, obj, message) => {
  if (value?.length !== 0) {
    const validFormats = ['doc', 'docx', 'pdf', 'ppt', 'pptx'];
    const getFormat = fileName => fileName?.split('.').pop();
    const invalidFormatFiles = value?.filter(({ name }) => !validFormats.includes(getFormat(name)));

    if (invalidFormatFiles.length !== 0) {
      const invalidFormats = invalidFormatFiles.map(({ name }) => `.${getFormat(name)}`);

      return obj.createError({
        message: `${message} (${invalidFormats.join(', ')})`,
      });
    }
  }
  return true;
};

const emojiValidation = value => {
  const regEx =
    /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}]/u;

  if (value && regEx.test(value)) {
    return false;
  }

  return true;
};

addMethod(string, 'checkEmail', function (message) {
  return this.test('checkEmail', message, async function (value) {
    return emailValidation(value);
  });
});

addMethod(string, 'checkBasicEmail', function (message) {
  return this.test('checkBasicEmail', message, function (value) {
    return basicEmailValidation(value);
  });
});

addMethod(string, 'checkCorporateEmail', function (message) {
  return this.test('checkCorporateEmail', message, function (value) {
    return corporateValidation(value);
  });
});

addMethod(string, 'checkPhone', function (message) {
  return this.test('checkPhone', message, function (value) {
    return phoneValidation(value);
  });
});

addMethod(mixed, 'checkFiles', function (messages) {
  return this.test('fileSize', function (value) {
    return fileSizeValidation(value, this, messages);
  }).test('fileType', function (value) {
    return fileTypeValidation(value, this, messages.type);
  });
});

addMethod(string, 'checkEmoji', function (message) {
  return this.test('checkEmoji', message, function (value) {
    return emojiValidation(value);
  });
});

const basicCorporateEmail = messages => {
  return string()
    .required(messages.required)
    .checkBasicEmail(messages.invalid)
    .checkCorporateEmail(messages.notCorporateEmail);
};

const corporateEmail = messages => {
  return string().required(messages.required).checkEmail(messages.invalid);
};

const phone = message => {
  return string().required(message).checkPhone(message);
};

const files = messages => {
  return mixed().required(messages.required).checkFiles(messages);
};

const name = noEmoji => string().checkEmoji(noEmoji);

const message = (messages, noEmoji) => string().required(messages).checkEmoji(noEmoji);

export const corporateFormSchema = messages => {
  return object().shape(
    {
      name: name(messages.emoji),
      email: corporateEmail(messages.email),
      phone: string().when('email', {
        is: value => basicCorporateEmail(messages.email).isValidSync(value),
        then: string(),
        otherwise: phone(messages.phone),
      }),
      message: message(messages.comment, messages.emoji),
      files: files(messages.files),
      NDA: boolean(),
    },
    ['email', 'phone'],
  );
};

export const eventRegistrationFormSchema = messages => {
  return object().shape(
    {
      name: name(messages.emoji),
      email: corporateEmail(messages.email),
      phone: string().when('email', {
        is: value => basicCorporateEmail(messages.email).isValidSync(value),
        then: string(),
        otherwise: phone(messages.phone),
      }),
      message: message(messages.comment, messages.emoji),
      NDA: boolean(),
    },
    ['email', 'phone'],
  );
};
